import { useGetAllConnectedQuery } from "../../features/connection/connectionApiSlice";
import { Col, Container } from "react-bootstrap";
import BackArrow from "../backArrow";
import CandidatesBox from "../candidatesBox";
import { useSelector, useDispatch } from "react-redux";
import ShopAdds from "../shopAds/ShopAdds";
import { useParams, useLocation } from "react-router-dom";
import Loader from "../Loader";
import { useState, useEffect, useMemo, useCallback } from "react";
import { io } from "socket.io-client";
import { SOCKET_URL } from "../../config/axios";
import { apiSlice } from "../../features/api/apiSlice";

const Connectionrequest = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { id: pathId } = useParams();
  const searchParams = new URLSearchParams(location.search);
  const isMarketerProfile = searchParams.get("isMarketerProfile") === "true";
  // Explicit shop scope from query wins; otherwise path :id is shop when it
  // is not the logged-in user id (business profile → /connection-request/:shopId).
  const shopIdFromQuery = searchParams.get("shopid");

  const { name } = useSelector((state) => state.user);
  const myUserId = name?._id ? String(name._id) : null;

  const pathIdStr =
    pathId && pathId !== "null" && pathId !== "undefined"
      ? String(pathId)
      : null;

  const shopScopeId = useMemo(() => {
    if (shopIdFromQuery) return shopIdFromQuery;
    if (pathIdStr && myUserId && pathIdStr !== myUserId) return pathIdStr;
    return null;
  }, [shopIdFromQuery, pathIdStr, myUserId]);

  const params = useMemo(() => {
    const next = {
      userid: myUserId,
    };

    if (shopScopeId) {
      next.shopid = shopScopeId;
    }

    if (isMarketerProfile) {
      next.isMarketerProfile = true;
    }

    return next;
  }, [myUserId, shopScopeId, isMarketerProfile]);

  const {
    data: responseData,
    isLoading: loading,
    isFetching,
    refetch: fetchPendingConnections,
  } = useGetAllConnectedQuery(params, {
    skip: !myUserId,
    refetchOnMountOrArgChange: true,
  });

  // Always pass real arrays so CandidatesBox can sync received/sent lists.
  // Support both flat and nested { data: { pending } } response shapes.
  const pendingData = useMemo(() => {
    const root =
      responseData?.pending != null || responseData?.requested != null
        ? responseData
        : responseData?.data || responseData || {};
    return {
      ...(root || {}),
      pending: Array.isArray(root?.pending) ? root.pending : [],
      requested: Array.isArray(root?.requested) ? root.requested : [],
    };
  }, [responseData]);
  const [activeTab, setActiveTab] = useState("received");

  const refreshLists = useCallback(() => {
    dispatch(apiSlice.util.invalidateTags(["UserConnections", "Connections"]));
  }, [dispatch]);

  // Real-time synchronization without polling or websockets via RTK Query tag invalidations:
  // Refetches when window regains focus or visibility changes
  useEffect(() => {
    if (!myUserId) return;

    const handleRealtimeUpdate = () => {
      refreshLists();
    };

    window.addEventListener("focus", handleRealtimeUpdate);
    document.addEventListener("visibilitychange", handleRealtimeUpdate);

    return () => {
      window.removeEventListener("focus", handleRealtimeUpdate);
      document.removeEventListener("visibilitychange", handleRealtimeUpdate);
    };
  }, [myUserId, refreshLists]);

  useEffect(() => {
    if (!myUserId) return;

    let socket;
    try {
      socket = io(SOCKET_URL);
      socket.emit("join_user", myUserId);

      // Real-time: other party accepted / new request arrived
      socket.on("notification", (data) => {
        if (
          data.type === "CONNECTION_REQUEST" ||
          data.type === "CONNECTION_ACCEPTED"
        ) {
          refreshLists();
        }
      });

      // Real-time: accept/cancel list sync for BOTH users
      socket.on("connection_list_updated", () => {
        refreshLists();
      });

      socket.on("connection_request_cancelled", () => {
        refreshLists();
      });
    } catch (err) {
      console.warn("Socket initialization skipped:", err);
    }

    return () => {
      if (socket) socket.disconnect();
    };
  }, [myUserId, refreshLists]);

  // Fresh fetch when opening from a shop / profile (avoids stale empty cache)
  useEffect(() => {
    if (!myUserId) return;
    fetchPendingConnections();
  }, [myUserId, shopScopeId, isMarketerProfile, fetchPendingConnections]);

  const listLoading = loading || (isFetching && !responseData);

  return (
    <div>
      <Container>
        <div className="screenHeight">
          <Col lg="9" className="mx-auto">
            <Col lg="12">
              <div className="d-flex justify-content-start">
                <BackArrow />
              </div>
              <div className="ps-2 pe-2">
                <ShopAdds />
              </div>
            </Col>

            <Col lg="12">
              <div className="ps-3 pe-3 mb-3 d-flex gap-3 border-bottom pb-2">
                <button
                  className={`btn ${activeTab === "received" ? "btn-primary" : "btn-light"}`}
                  onClick={() => setActiveTab("received")}
                >
                  Received Requests
                </button>
                <button
                  className={`btn ${activeTab === "sent" ? "btn-primary" : "btn-light"}`}
                  onClick={() => setActiveTab("sent")}
                >
                  Sent Requests
                </button>
              </div>
              <div className="ps-3 pe-3">
                {listLoading ? (
                  <Loader />
                ) : (
                  <CandidatesBox
                    pendingData={pendingData}
                    loading={listLoading}
                    fetchPendingConnections={fetchPendingConnections}
                    activeTab={activeTab}
                  />
                )}
              </div>
            </Col>
          </Col>
        </div>
      </Container>
    </div>
  );
};

export default Connectionrequest;
